/* ===========================================================================
   ROCKSOLAR product finder — the engine
   ---------------------------------------------------------------------------
   assets/rs-finder-engine.js

   Loaded after assets/rs-finder-catalog.js, which reads the live catalogue out
   of the page and leaves it on window.RS_TABLES. This file is the part that
   asks the questions and works out the answer. It never names a product and
   never reads a metafield: everything it knows about the range arrives in T.
   =========================================================================== */
window.RS_ENGINE = function (T) {
  "use strict";

  var CATALOG = T.CATALOG, PUMPCURVES = T.PUMPCURVES, POOL = T.POOL, RACKING = T.RACKING;
  var rackFor = T.rackFor, catalogFamily = T.catalogFamily, chooseSystem = T.chooseSystem;
  var kwLabel = T.kwLabel, kwhLabel = T.kwhLabel, money = T.money, KW_TOL = T.KW_TOL;
  /* Where the prototype's in-page anchors point on the live store. The section
     can override both; these are the fallbacks. */
  var body  = document.getElementById("selBody");
  var crumb = document.getElementById("selCrumb");
  var stage = document.getElementById("selStage");

  var LINKS     = T.links || {};
  var L_SUPPORT = LINKS.support || "/pages/contact";
  var L_CATALOG = LINKS.catalog || "/collections/all";

  /* Add to cart without a form or a fetch. Shopify's cart permalink takes
     variant:quantity pairs, adds them and lands the customer in the cart, so
     the button is an ordinary link that works with JavaScript half-loaded and
     survives a middle-click into a new tab.
     items: [[variantGid, qty], ...]. A row with no variant id is skipped; if
     nothing is left the caller's fallback URL is used instead. */
  function cartLink(items, fallback) {
    var parts = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it || !it[0]) { continue; }
      var id = String(it[0]).replace(/\D/g, "");
      if (id) { parts.push(id + ":" + (it[1] || 1)); }
    }
    return parts.length ? "/cart/" + parts.join(",") + "?storefront=true" : (fallback || L_CATALOG);
  }

  /* The pool pump table, the pump curve table, the system catalog and the
     racking table all used to be written out here by hand. They are gone: the
     adapter builds all four from live products before this function is called.
     What is left below is the engineering — the trade figures and the physics
     that turn a customer's answers into a duty. Those are not product data and
     do not belong in the store.

     One thing worth keeping in mind while reading it: every pool pump in the
     range is variable speed (2500–4200 RPM). You size the pump so it CAN reach
     the turnover, then run it at whatever speed the filter and the pipework
     will take. A larger pump run slowly beats a small one at full tilt on both
     noise and power. */

  /* What the rest of the plumbing costs you in head. These are the standard trade
     allowances for a residential pool; a roof-mounted solar heating array is the big
     one, because you are lifting the water to the roof as well as pushing it through
     the panels. */
  var POOL_HEADS = {
    above:   {ft:20, label:"Above-ground pool, pump beside it, short hoses"},
    simple:  {ft:30, label:"Filter only, short runs, pump near pool level"},
    typical: {ft:40, label:"Filter plus a tablet or liquid chlorinator"},
    heater:  {ft:50, label:"Filter, chlorinator and a heater"},
    solar:   {ft:70, label:"Plus roof-mounted solar heating panels"},
    unknown: {ft:45, label:"Not sure — we assume a typical 45 ft system"}
  };

  /* ---------------------------------------------------------------
     Pool sizing. Volume sets what you NEED; the filter and the pipe set
     what the system can actually take, and the smaller of those two governs.

     Turnover:  required GPM = gallons / (hours x 60). Eight hours is the
                residential convention.
     Filter:    every filter has a design flow in GPM per square foot of media —
                sand 15, cartridge 0.5, DE 2 are the standard trade figures. Push
                more through it and the water channels past the media instead of
                being filtered, which is worse than a slower turnover.
     Pipe:      water above about 6 ft/s on the suction side cavitates, erodes
                fittings and howls. Q = 6 x d^2 / 0.4085 for Schedule 40 PVC.
     --------------------------------------------------------------- */
  var TURNOVER_H = 8;
  var POOL_FILTERS = {
    "sand-s":  {label:"Sand filter, 19–21 in tank",       gpm:30,  note:"about 2 sq ft of sand at 15 GPM per sq ft"},
    "sand-m":  {label:"Sand filter, 22–24 in tank",       gpm:47,  note:"about 3.1 sq ft of sand at 15 GPM per sq ft"},
    "sand-l":  {label:"Sand filter, 26–30 in tank",       gpm:74,  note:"about 4.9 sq ft of sand at 15 GPM per sq ft"},
    "cart-s":  {label:"Cartridge filter, 100–200 sq ft",  gpm:75,  note:"150 sq ft at 0.5 GPM per sq ft"},
    "cart-l":  {label:"Cartridge filter, 300 sq ft or more", gpm:150, note:"300 sq ft at 0.5 GPM per sq ft"},
    "de":      {label:"DE filter, 36–48 sq ft",           gpm:96,  note:"48 sq ft at 2 GPM per sq ft"},
    "none":    {label:"Not sure, or no filter yet",       gpm:null,note:"we will size the filter with you"}
  };
  var POOL_PIPES = {
    "1.5":  {label:"1½ inch", gpm:38,  id:1.610},
    "2":    {label:"2 inch",  gpm:63,  id:2.067},
    "2.5":  {label:"2½ inch", gpm:89,  id:2.469},
    "3":    {label:"3 inch or larger", gpm:138, id:3.068},
    "none": {label:"Not sure", gpm:null, id:null}
  };

  function sizePool(gallons, filterKey, pipeKey, headKey){
    var need   = gallons / (TURNOVER_H * 60);
    var filter = POOL_FILTERS[filterKey] || POOL_FILTERS.none;
    var pipe   = POOL_PIPES[pipeKey]     || POOL_PIPES.none;
    var sysHead = (POOL_HEADS[headKey] || POOL_HEADS.unknown);
    var caps   = [filter.gpm, pipe.gpm].filter(function(v){ return v !== null; });
    var cap    = caps.length ? Math.min.apply(null, caps) : null;
    var binder = cap === null ? null : (filter.gpm === cap ? "filter" : "pipe");
    /* If the cap is below the turnover requirement, the pool cannot be turned over in
       eight hours at all — so say how long it will actually take, rather than selling a
       pump that will be throttled anyway. */
    var hours  = (cap !== null && cap < need) ? gallons / (cap * 60) : TURNOVER_H;
    return {gallons:gallons, need:need, filter:filter, pipe:pipe, cap:cap, head:sysHead,
            binder:binder, hours:hours, throttled:(cap !== null && cap < need)};
  }

  /* The questions, and the documents each result can offer, live in
     assets/rs-finder-content.js — they are copy, not logic, and that file is
     the one to edit to change what the finder asks or how it explains itself.
     If it fails to load the finder says so rather than throwing. */
  var CONTENT = window.RS_CONTENT || {};
  var FLOWS = CONTENT.FLOWS, DOCS = CONTENT.DOCS || [];
  if(!FLOWS){
    if(body) body.innerHTML = '<p class="sel-help">The finder could not load its questions. ' +
      '<a href="' + L_CATALOG + '">Browse the range</a>, or ' +
      '<a href="' + L_SUPPORT + '">tell us what you are building</a> and we will size it with you.</p>';
    return;
  }


  /* ---------------------------------------------------------------
     Pump sizing — real hydraulics, not a lookup table.

     A pump is chosen by TOTAL DYNAMIC HEAD, not by horsepower. TDH is the
     vertical lift plus the friction the water fights on the way along the pipe.
     Friction is Hazen-Williams for Schedule 40 PVC at C=150, which agrees with
     published pipe tables to within a few percent:
        ft per 100 ft = 0.09778 x Q^1.852 / d^4.8655   (Q in GPM, d = inside dia.)
     Pipe is chosen as the smallest size keeping velocity at or under 5 ft/s;
     above that you start paying for friction and hammering the fittings.
     Fittings add a 10% allowance on top of straight-pipe loss.

     Pump output at a given head uses a STRAIGHT LINE between the two figures
     ROCKSOLAR publishes for each model — max flow at zero head, and max head at
     zero flow. Real curves bulge above that line (the 1.34 HP curve in the
     manual gives 10.04 GPM at half its max head where a straight line predicts
     8.85), so this deliberately UNDERSTATES every pump. A model that qualifies
     here will beat the figure in the ground.
     --------------------------------------------------------------- */
  var PIPES = [["1″",1.049],["1¼″",1.380],["1½″",1.610],
               ["2″",2.067],["2½″",2.469],["3″",3.068],["4″",4.026]];
  var VEL_MAX = 5;      /* ft/s */
  var FITTINGS = 1.10;  /* allowance over straight-pipe loss */
  var SUCTION_LIMIT = 20; /* ft — practical suction lift for a surface pump */

  function pickPipe(q){
    for(var i=0;i<PIPES.length;i++){
      var v = 0.4085*q/(PIPES[i][1]*PIPES[i][1]);
      if(v <= VEL_MAX) return {label:PIPES[i][0], id:PIPES[i][1], vel:v};
    }
    var last = PIPES[PIPES.length-1];
    return {label:last[0], id:last[1], vel:0.4085*q/(last[1]*last[1])};
  }
  function frictionPer100(q, d){ return 0.09778 * Math.pow(q,1.852) / Math.pow(d,4.8655); }

  function sizePump(lift, dist, gpm){
    var pipe = pickPipe(gpm);
    var per100 = frictionPer100(gpm, pipe.id);
    var friction = per100 * (dist/100) * FITTINGS;
    return {lift:lift, dist:dist, gpm:gpm, pipe:pipe, per100:per100,
            friction:friction, tdh:lift + friction};
  }

  /* Pump curves are not straight lines, and treating them as one costs the customer
     real money — it pushed a 15 GPM job at 277 ft onto a 4 HP pump when a 3 HP does it.
     CURVE is the measured shape from the published curve in the ROCKSOLAR 1.34 HP manual
     (11 points, GPM against feet), normalised to fractions of max flow and max head. It
     sits 3–17% above a straight line across the middle of the range, which is what a real
     centrifugal curve does.
     The assumption — and it is one — is that the other models share this shape. It is
     ROCKSOLAR's own published data rather than a guess, but the manual's own curve is
     what an installer should size against on a marginal job, and the page says so. */
  var CURVE = [[0,1],[0.1006,0.9266],[0.1982,0.8508],[0.2988,0.7763],[0.3994,0.7017],
               [0.5,0.5672],[0.6006,0.5220],[0.6982,0.4181],[0.7988,0.3429],[0.8994,0.2689],[1,0]];

  function curveFrac(hf){
    if(hf <= 0) return 1;
    if(hf >= 1) return 0;
    for(var i=1;i<CURVE.length;i++){
      if(hf <= CURVE[i][0]){
        var a = CURVE[i-1], b = CURVE[i];
        var t = (hf - a[0]) / (b[0] - a[0]);
        return a[1] + t*(b[1] - a[1]);
      }
    }
    return 0;
  }

  function flowAtHead(model, head){
    var qmax = model[1], hmax = model[2];
    if(head >= hmax) return 0;
    return qmax * curveFrac(head/hmax);
  }


  /* Returns every model that delivers the required flow at the computed head,
     cheapest first, in-stock first. */
  /* drive: "dc" restricts to solar-only pumps. A hybrid AC/DC pump is only worth
     recommending when there is grid or generator power at the site to fall back on —
     otherwise the customer pays for an AC side that will never be connected. */
  function matchPumps(sz, kind, wantKit, drive){
    var out = [];
    PUMPCURVES.forEach(function(p){
      if(kind && p[1] !== kind) return;
      if(drive && p[2] !== drive) return;
      if(wantKit !== undefined && p[4] !== wantKit && !p[5]) return;
      p[6].forEach(function(m){
        var q = flowAtHead(m, sz.tdh);
        if(q < sz.gpm) return;
        out.push({name:p[0], kind:p[1], drive:p[2], hp:p[3], kit:p[4], sys:p[5],
                  model:m[0], qmax:m[1], hmax:m[2], price:m[3], stock:m[4],
                  variantId:m[5], url:p[7].url, image:p[7].image, docs:p[7].docs,
                  qAt:q, margin:q/sz.gpm});
      });
    });
    out.sort(function(a,b){
      if((b.stock>0) !== (a.stock>0)) return (b.stock>0) - (a.stock>0);
      return a.price - b.price;
    });
    return out;
  }

  /* ---- Resolution ---- */
  function resolvePool(a){
    /* Salt water is a hard stop, not a caveat. These pumps are not rated for it, and
       selling one into a salt pool costs the customer a pump and us the warranty claim.
       Say so plainly and early rather than sizing something we cannot supply. */
    if(a.water === "salt"){
      return {blocked:true,
        title:"We do not have a pump for a salt water pool",
        because:"A salt water pool still runs on chlorine — the cell makes it from dissolved salt, usually around 3,000 ppm. <b>Our pool pumps are not rated for that.</b> We could size one for your pool in a minute, and it would move the water beautifully for a season or two, and then it would not. We would rather lose the sale than sell you that.",
        alt:{t:"What we would suggest instead",
             b:"Look for a pump the manufacturer <b>explicitly rates for salt water</b> — not one that merely does not mention it. The distinction matters: the wetted parts, the seal and the hardware all have to be specified for it, and a pump that is silent on the subject usually is not. If you tell us your pool size, filter and plumbing we will tell you what specification to shop for, at no cost and with nothing to sell you. And if you are on a <b>freshwater</b> pool after all, start over — we can size that in four questions."}};
    }
    var sz = sizePool(a.size, a.filter, a.pipe, a.head);
    var H  = sz.head.ft;

    /* Size the pump to what the system can actually pass, not to the pool volume alone —
       and read its flow off the curve AT THE SYSTEM HEAD, not at the headline figure.
       Curve shape is the measured one from the ROCKSOLAR pump manual, scaled to each
       pump's own published max flow and max head. */
    var target = sz.cap !== null ? Math.min(sz.need, sz.cap) : sz.need;
    var atHead = function(p){ return flowAtHead(["x", p.gpm, p.head], H); };
    var pick = null;
    for(var i=0;i<POOL.length;i++){ if(atHead(POOL[i]) >= target){ pick = POOL[i]; break; } }
    var short = false;
    if(!pick){ pick = POOL[POOL.length-1]; short = true; }
    var deliver = atHead(pick);
    var realHours = deliver > 0 ? sz.gallons / (deliver * 60) : Infinity;

    /* Solar-only unless the customer asked for grid fallback — same rule as the water
       pumps. The 0.5 HP has no hybrid variant, so if they asked for one and this is the
       pump the duty needs, say so rather than silently handing them a solar-only pump. */
    var unsureWater = (a.water === "unsure");
    var wantHy  = (a.power === "hybrid");
    var hyGone  = wantHy && !pick.hy;
    var drv     = (wantHy && pick.hy) ? pick.hy : pick.dc;
    var driveL  = (wantHy && pick.hy) ? "Hybrid — solar with grid AC fallback" : "Solar / DC only";

    /* Same shape the water-pump chooser uses, so one renderer serves both. */
    var POOLBUY = [
      {id:"pump", label:"Pump only", row:{price:drv.pump.p, stock:drv.pump.s, variantId:drv.pump.variantId},
       sub:"You already have panels up, or an array with capacity to spare."},
      {id:"kit",  label:"Pump and panels", row:{price:drv.kit.p, stock:drv.kit.s, variantId:drv.kit.variantId},
       sub:"The usual choice. Array sized for this pump, with the cable made off."}
    ];
    var chosen  = POOLBUY.filter(function(b){ return b.id === state.pumpBuy; })[0] || POOLBUY[1];
    var kitOnly = chosen.id === "pump";
    var price   = chosen.row.price;
    var stockN  = chosen.row.stock;

    var head = '<div class="tdh">' +
      '<div class="tdh-head"><span class="eyebrow">How we got there</span>' +
        '<b>' + Math.round(target) + ' GPM wanted, ' + deliver.toFixed(0) + ' GPM delivered at ' + H + ' ft of head</b></div>' +
      '<div class="tdh-rows">' +
        '<div class="spec-row"><span>' + sz.gallons.toLocaleString("en-US") + ' gal turned over once in ' + TURNOVER_H + ' hours</span><span>' + sz.need.toFixed(0) + ' GPM</span></div>' +
        '<div class="spec-row"><span>Your filter — ' + esc(sz.filter.label) + '</span><span>' +
          (sz.filter.gpm === null ? 'not known' : 'max ' + sz.filter.gpm + ' GPM') + '</span></div>' +
        '<div class="spec-row"><span>Your plumbing — ' + esc(sz.pipe.label) + ' at 6 ft/s</span><span>' +
          (sz.pipe.gpm === null ? 'not known' : 'max ' + sz.pipe.gpm + ' GPM') + '</span></div>' +
        '<div class="spec-row tdh-total"><span>' +
          (sz.cap === null ? 'Nothing capping it yet, so we size on turnover'
           : (sz.binder === "filter" ? 'Governed by the filter' : 'Governed by the pipe')) +
          '</span><span>' + Math.round(target) + ' GPM</span></div>' +
        '<div class="spec-row"><span>System head — ' + esc(sz.head.label.toLowerCase()) + '</span><span>' + H + ' ft</span></div>' +
        '<div class="spec-row"><span>This pump’s rating, wide open</span><span>' + pick.gpm + ' GPM at 0 ft, ' + pick.head + ' ft max</span></div>' +
        '<div class="spec-row tdh-total"><span>What it actually delivers at ' + H + ' ft</span><span>' + deliver.toFixed(0) + ' GPM</span></div>' +
      '</div>' +
      '<p class="tdh-note">Filter design flow: sand 15 GPM per sq ft, DE 2, cartridge 0.5 — the standard trade figures. Pipe limit is 6 ft/s on the suction side, where water starts to cavitate and chew out fittings. ' +
      (sz.filter.gpm !== null ? 'Our filter figure assumes ' + esc(sz.filter.note) + '; if the label is still on your tank, use that number instead. ' : '') +
      'Flow at head is read off the curve shape published in the ROCKSOLAR pump manual, scaled to this pump’s own maximum flow and maximum head — an estimate, not a measured curve for this model. Every pump here is variable speed (2500–4200 RPM), so the figure above is the speed you run it at, not the pump you must buy.</p>' +
    '</div>';

    var why;
    if(short){
      why = "At <b>" + H + " ft of head</b> even the largest pump we stock delivers <b>" + deliver.toFixed(0) +
        " GPM</b>, short of the " + Math.round(target) + " GPM this pool could take. It still works — the turnover simply takes <b>" +
        (isFinite(realHours) ? realHours.toFixed(1) + " hours" : "longer than a day") +
        "</b> rather than eight. If that is not good enough, the fix is to take head out of the system: shorter runs, bigger pipe, a cleaner filter.";
    } else if(sz.throttled){
      why = "Your pool wants <b>" + sz.need.toFixed(0) + " GPM</b> for an eight-hour turnover, but your " +
        (sz.binder === "filter" ? "filter" : "plumbing") + " will only pass <b>" + sz.cap + " GPM</b>. " +
        "No pump changes that — push harder and you " +
        (sz.binder === "filter" ? "channel water past the media instead of through it" : "cavitate the suction line and start eating fittings") +
        ". So run this one at " + sz.cap + " GPM and let the turnover take <b>" + sz.hours.toFixed(1) + " hours</b> instead of eight. " +
        "That is the honest answer, and it costs nothing.";
    } else if(sz.cap === null){
      why = "You are building new or did not know the filter and pipe, so this is sized on turnover alone: <b>" +
        sz.need.toFixed(0) + " GPM</b> to move " + sz.gallons.toLocaleString("en-US") + " gallons once in eight hours. " +
        "Before you order, tell us the filter and pipe you plan to use — either one can cap the flow below this and change the answer.";
    } else {
      why = "Your pool needs <b>" + sz.need.toFixed(0) + " GPM</b> for an eight-hour turnover, and your " +
        esc(sz.filter.label.toLowerCase()) + " and " + esc(sz.pipe.label.toLowerCase()) + " plumbing will pass up to <b>" +
        sz.cap + " GPM</b> — so the turnover is what governs, with headroom to spare. This " + pick.hp +
        " HP pump is rated at " + pick.gpm + " GPM at full speed; you will run it slower than that, which is quieter and cheaper than a smaller pump at full tilt.";
    }
    if(!short){
      why += " Its headline figure is " + pick.gpm + " GPM, but that is wide open with nothing attached; against your " +
             H + " ft of head it gives <b>" + deliver.toFixed(0) + " GPM</b>, and that is the number the turnover is built on.";
    }
    if(unsureWater){
      why += " <b>Check one thing before you order.</b> You were not sure whether the pool is salt water. If you buy bags of salt for it, it is — and these pumps are not rated for salt water, so this recommendation does not apply. It takes a second to check the label on the equipment pad, and it saves a pump.";
    }
    if(hyGone){
      why += " <b>One thing you asked for is not available here.</b> You wanted grid fallback, but the " + pick.hp +
             " HP is the only pump in the pool range with no hybrid variant — it is solar-only, so there is no AC input to wire. " +
             "Either take it as it is, or step up to the 1.07 HP, which offers hybrid and is a stronger pump.";
    }
    if(pick.port < 2.4 && deliver > 63){
      why += " <b>Check the plumbing at the pump.</b> This model has " + pick.port +
             "-inch ports, which carry about 63 GPM before the water is moving faster than 6 ft/s. Run it below that speed, or upsize the pipe at the pump.";
    }

    return {
      save:{mode:"pool", kwh:pick.kwh, cost:price},
      pool:sz, head:head, buy:POOLBUY, buyId:chosen.id,
      variantId: chosen.row.variantId, url: pick.url,
      image: pick.image, docs: pick.docs,
      because:why,
      name: pick.base + (kitOnly ? "" : " Kit") + (wantHy && pick.hy ? " — hybrid AC/DC" : " — solar only"),
      price: price, stock: stockN,
      specs:[
        ["Flow at your system head", deliver.toFixed(0) + " GPM at " + H + " ft"],
        ["Turnover at that flow", isFinite(realHours) ? realHours.toFixed(1) + " hours" : "over 24 hours"],
        ["Pump rating", pick.hp + " HP — " + pick.gpm + " GPM wide open, " + pick.head + " ft max head"],
        ["Inlet and outlet", pick.port + " inch"],
        ["Speed control", "Variable, 2500–4200 RPM"],
        ["Power input", driveL],
        ["Supplied as", kitOnly ? "Pump only" : "Complete kit"]
      ],
      includes: (kitOnly
        ? ["Pool pump with variable-speed controller","Union fittings and strainer basket","Installation manual with pre-start checklist (PDF)"]
        : ["Pool pump with variable-speed controller","Solar panels sized for a full daily turnover","Pre-terminated DC cable","Installation manual with pre-start checklist (PDF)"])
        .concat(wantHy && pick.hy ? ["AC input wired to the controller, switching automatically when solar is short"] : []),
      alt: sz.throttled
        ? {t: sz.binder === "filter" ? "Worth pricing a bigger filter" : "Worth pricing bigger pipe",
           b: sz.binder === "filter"
              ? "A larger filter would let this pool turn over in " + TURNOVER_H + " hours instead of " + sz.hours.toFixed(1) +
                ". On a pool this size that is usually a few hundred dollars and it makes every future pump work properly — worth doing before you buy the pump, not after."
              : "Going up one pipe size would let this pool turn over in " + TURNOVER_H + " hours instead of " + sz.hours.toFixed(1) +
                ". Pipe and fittings are cheap next to a pump; being stuck at " + sz.cap + " GPM for the life of the pool is not."}
        : (wantHy && pick.hy)
        ? {t:"Is the $200 for hybrid worth it on a pool?", b:"It buys you circulation on dull days and after dark. On a pool that matters less than on a well — a few hours of missed turnover does no harm, and the solar-only version of this pump is "+money(pick.dc[chosen.id].p)+". Take hybrid if the pool is heated, heavily used, or somewhere with long grey spells; otherwise keep the $200."}
        : !pick.hy
        ? {t:"This size is solar-only", b:"The "+pick.hp+" HP is the one pump in the pool range with no hybrid variant — there is no AC input to add. If falling back to grid power matters to you, the next size up offers it for "+money(POOL[1].hy[chosen.id].p)+", and it is a stronger pump besides."}
        : {t:"Want it to keep running when the sun does not?", b:"The hybrid version of this pump is "+money(pick.hy[chosen.id].p)+", a flat $200 more, and switches to grid AC automatically when solar is short. Only worth it if there is mains power at the pump — and on most pools, missing an hour of circulation costs nothing."}
    };
  }

  function resolveWell(a){
    var lift = a.lift, dist = a.dist, gpm = a.gpm;
    var sz   = sizePump(lift, dist, gpm);
    var isWell = a.source === "well";

    /* The limit on a surface pump is SUCTION lift — how far it can pull water UP to
       reach itself, about 20 ft, because that is atmospheric pressure and no amount of
       horsepower changes it. It is NOT a limit on how far the pump can PUSH afterwards.
       An earlier version of this page confused the two and sent anyone drawing from a
       creek up a long hill to a submersible, when a surface pump sitting at the water's
       edge would have done the same job for a fraction of the money. So the siting
       question decides the family, and total lift only decides which model. */
    var shallow      = (a.water === "shallow" || a.water === "shallow-high");
    var mustSubmerse = isWell || a.water === "deep-high";
    var surfaceOK    = !mustSubmerse;

    /* Solar-only unless the customer told us there is grid or generator power to fall
       back on. If they did ask for it, give them a hybrid — and only drop back to a
       DC pump when no hybrid reaches the duty, saying so when that happens. */
    var wantHybrid = (a.power === "hybrid");
    var drive      = wantHybrid ? "hybrid" : "dc";
    var subHits    = matchPumps(sz, "sub", undefined, drive);
    var transHits  = surfaceOK ? matchPumps(sz, "trans", undefined, drive) : [];
    var noHybrid   = false;
    if(wantHybrid && !subHits.length && !transHits.length){
      noHybrid  = true;
      subHits   = matchPumps(sz, "sub", undefined, "dc");
      transHits = surfaceOK ? matchPumps(sz, "trans", undefined, "dc") : [];
    }

    /* Under about 5 ft of water a submersible cannot stay covered, so a surface pump
       comes first whatever the price — and only if no surface pump reaches the duty do
       we fall back to a submersible, which then needs a sump or flow sleeve to keep it
       under water and out of the silt. Where the water is deep enough for either, price
       decides and the other family is named honestly beside it. */
    var hits, other = null, swapped = false, sumpNeeded = false;
    if(shallow && transHits.length){
      hits = transHits;
      if(subHits.length) other = subHits[0];
    } else if(shallow){
      hits = subHits; sumpNeeded = subHits.length > 0;
    } else if(transHits.length && subHits.length){
      var cheapT = transHits[0], cheapS = subHits[0];
      if(cheapT.price <= cheapS.price){ hits = transHits; other = cheapS; }
      else { hits = subHits; other = cheapT; }
    } else if(transHits.length){ hits = transHits; }
    else { hits = subHits; if(surfaceOK && subHits.length) swapped = true; }

    var srcWord = {well:"a drilled well", lake:"a lake or pond", stream:"a stream or river", tank:"a tank or cistern"}[a.source];

    /* Shallow water and a pump that has to sit high is the one combination no pump
       solves as it stands: too shallow to submerge, too high to suck. The answer is a
       change to the site, not a different product, so say that instead of selling one. */
    var siteFix = (a.water === "shallow-high")
      ? '<div class="res-because" style="border-left-color:var(--alert,#A6231C);background:var(--alert-wash,#FBECEA)">' +
        '<b>This needs a change to the site, not a bigger pump</b>' +
        'Water under about 5 ft deep is too shallow to keep a submersible covered, and a bank more than ' + SUCTION_LIMIT +
        ' ft above the water is more than any surface pump can pull up to itself. Nothing on the market squares that circle. ' +
        'Two things do: bring the pump <b>down to the water</b> and push from there — a surface pump on a platform at the edge, ' +
        'wired back up the bank — or dig a <b>sump beside the watercourse</b> that fills through gravel and gives a submersible ' +
        'the depth it needs. The pump below assumes you do one of those. Tell us which and we will confirm it before you order.' +
        '</div>'
      : "";

    var head = '<div class="tdh">' +
      '<div class="tdh-head"><span class="eyebrow">How we got there</span>' +
        '<b>' + sz.tdh.toFixed(0) + ' ft of total dynamic head at ' + gpm + ' GPM</b></div>' +
      '<div class="tdh-rows">' +
        '<div class="spec-row"><span>Vertical lift you gave us</span><span>' + lift.toLocaleString("en-US") + ' ft</span></div>' +
        '<div class="spec-row"><span>Pipe we would run — ' + sz.pipe.label + ' Sch 40 PVC, ' + sz.pipe.vel.toFixed(1) + ' ft/s</span><span>' + dist.toLocaleString("en-US") + ' ft</span></div>' +
        '<div class="spec-row"><span>Friction along that run, plus 10% for fittings</span><span>' + sz.friction.toFixed(0) + ' ft</span></div>' +
        '<div class="spec-row tdh-total"><span>Total dynamic head</span><span>' + sz.tdh.toFixed(0) + ' ft</span></div>' +
      '</div>' +
      '<p class="tdh-note">Friction is Hazen-Williams for Schedule 40 PVC at C=150 — the same arithmetic a pipe table uses. Pipe size is the smallest that keeps velocity at or under ' + VEL_MAX + ' ft/s. Flow at head is read off the curve shape published in the ROCKSOLAR pump manual, scaled to each model’s own maximum flow and maximum head. It is an estimate: on a job this close to a limit, size against the exact curve in that model’s manual, or send us the numbers and we will do it.</p>' +
    '</div>';

    if(!hits.length){
      return {
        project:false, nofit:true, tdh:sz, head:head,
        because:"Nothing in the range reaches " + sz.tdh.toFixed(0) + " ft of head at " + gpm + " GPM. That is a real answer, not a gap in the catalogue — past this point you are into staged pumping, a booster, or a bigger pipe to buy back friction.",
        alt:{t:"What to try", b:"Going one pipe size up costs less than a bigger pump and can pull the head back under the limit — tell us the run and we will work it out with you. If the lift is genuinely that high, the answer is usually two pumps in series with a break tank, which we can specify."}
      };
    }

    /* Three ways to buy the same pump. Most people want the kit — the panels are already
       matched to the controller and there is nothing to work out on site — so that is the
       default when one exists. The bare pump is for someone adding to an array that is
       already up; the complete system adds storage so it pumps before dawn and after dusk.
       A variant only appears if it actually reaches this duty. */
    var lead = hits[0];
    function sameModel(h){
      return h.kind === lead.kind && h.drive === lead.drive && h.hp === lead.hp &&
             Math.abs(h.qmax - lead.qmax) < 0.01 && Math.abs(h.hmax - lead.hmax) < 0.01;
    }
    function firstOf(test){
      var m = hits.filter(function(h){ return sameModel(h) && test(h); });
      if(!m.length) m = hits.filter(function(h){ return h.kind===lead.kind && h.drive===lead.drive && h.hp===lead.hp && test(h); });
      return m.sort(function(x,y){ return (y.stock>0)-(x.stock>0) || x.price-y.price; })[0] || null;
    }
    var vPump   = firstOf(function(h){ return !h.kit && !h.sys; });
    var vKit    = firstOf(function(h){ return  h.kit && !h.sys; });
    var vSystem = firstOf(function(h){ return  h.sys; });

    var BUY = [];
    if(vPump)   BUY.push({id:"pump",   label:"Pump only",        row:vPump,
                          sub:"You already have panels up, or you are adding this to an array that exists."});
    if(vKit)    BUY.push({id:"kit",    label:"Pump and panels",  row:vKit,
                          sub:"The usual choice. Panels sized and matched to this pump’s controller, so nothing has to be worked out on site."});
    if(vSystem) BUY.push({id:"system", label:"Complete system",  row:vSystem,
                          sub:"Adds battery storage, so it pumps before dawn and after dusk rather than only while the sun is on it."});

    /* The cross-family comparison has to be like for like. `other` was picked as the
       cheapest of the opposite family before the customer chose how to buy, so once
       they switch to pump-only it was comparing a bare pump against a full kit — and
       could report "$0.00 more". Re-pick it in the same form as whatever they chose. */
    var otherFamily = other ? (other.kind === "sub" ? "sub" : "trans") : null;

    var wantBuy = state.pumpBuy;
    var chosen  = BUY.filter(function(b){ return b.id === wantBuy; })[0]
               || BUY.filter(function(b){ return b.id === "kit"; })[0]
               || BUY[0];
    var pick = chosen ? chosen.row : lead;
    if(otherFamily){
      var form = chosen ? chosen.id : "kit";
      var pool = (otherFamily === "sub" ? subHits : transHits).filter(function(h){
        return form === "system" ? h.sys : (form === "kit" ? (h.kit && !h.sys) : (!h.kit && !h.sys));
      });
      other = pool.sort(function(x,y){ return (y.stock>0)-(x.stock>0) || x.price-y.price; })[0] || null;
    }
    var kit  = vKit;
    var margin = Math.round((pick.margin - 1) * 100);

    var why = "You are pulling from " + srcWord + " and asking for " + gpm + " GPM " +
      (lift > 0 ? "up " + lift.toLocaleString("en-US") + " ft" : "on the level") +
      (dist > 0 ? " and along " + dist.toLocaleString("en-US") + " ft of pipe" : "") + ". " +
      "That works out at <b>" + sz.tdh.toFixed(0) + " ft of total dynamic head</b>, and this " + pick.hp +
      " HP " + (pick.kind === "sub" ? "submersible" : "surface transfer") + " pump delivers about " +
      pick.qAt.toFixed(1) + " GPM there — " + (margin > 0 ? margin + "% more than you asked for" : "right on your figure") + ".";

    if(pick.drive === "hybrid"){
      why += " You have power at the site, so this is the hybrid version: it runs on solar while the sun is up and switches to grid or generator when it is not, which means it keeps pumping after dark.";
    } else if(noHybrid){
      why += " You asked for grid or generator fallback, but no hybrid pump in the range reaches this duty — the AC/DC models top out below it. This is the solar-only pump that does the job. If running after dark matters more than the head, tell us and we will look at a smaller duty with storage instead.";
    } else if(a.power === "solar"){
      why += " With no other power at the site this is a solar-only pump — no AC side to pay for and nothing to connect it to. It pumps while the sun is up, so size your tank for a day or two of storage rather than expecting it overnight.";
    }
    if(pick.kind === "trans" && shallow){
      why += " Under about 5 ft of water a submersible cannot stay covered — it starts pulling air and silt, which is what wears them out — so this is a surface pump standing on the bank. It only has to <b>pull</b> the water a few feet up to itself; everything after that it <b>pushes</b>, which is why it can serve a tank well up a hill and still cost less than a submersible.";
    } else if(pick.kind === "trans"){
      why += " It sits on the bank rather than in the water: with the pump near the water level it only has to <b>pull</b> a few feet, and everything after that it <b>pushes</b> — which is why a surface pump can serve a tank well up a hill and still cost less than a submersible.";
    } else if(sumpNeeded){
      why += " <b>One thing to sort out first.</b> No surface pump in the range reaches this duty, so this is a submersible — but you told us the water is under about 5 ft deep, and a submersible sitting in that will draw air and silt. It needs a sump, a flow sleeve or a length of pipe set into the bed to keep it covered and off the bottom. That is straightforward to build, but do not skip it: running one uncovered is the fastest way to ruin it.";
    } else if(a.water === "deep-high"){
      why += " You said the pump would sit more than " + SUCTION_LIMIT + " ft above the water, or in it. Past " + SUCTION_LIMIT + " ft nothing can pull water up to itself — that is atmospheric pressure, not horsepower — so this has to be a submersible.";
    } else if(swapped){
      why += " No surface pump in the range reaches this head, so this is a submersible instead.";
    }

    var others = hits.slice(1, 4).filter(function(h){ return h.name !== pick.name; });

    return {
      save:{mode:"farm", kwh:Math.round(pick.hp * 746 * 6 * 300 / 1000), cost:pick.price},
      tdh:sz, head:head, hitCount:hits.length, others:others, siteFix:siteFix, offer:"",
      buy:BUY, buyId:(chosen ? chosen.id : null),
      variantId: pick.variantId, url: pick.url,
      image: pick.image, docs: pick.docs,
      because:why,
      name: pick.name + (pick.model !== "-" ? " — " + pick.model : ""),
      price: pick.price, stock: pick.stock,
      specs:[
        ["Total dynamic head", sz.tdh.toFixed(0) + " ft"],
        ["Flow at that head", pick.qAt.toFixed(1) + " GPM"],
        ["Pump rating", pick.hp + " HP"],
        ["Pump type", pick.kind === "sub" ? "Submersible, brushless" : "Surface transfer, brushless"],
        ["Power input", pick.drive === "hybrid" ? "Solar DC or grid AC" : "Solar DC"],
        ["Recommended pipe", sz.pipe.label + " Sch 40 PVC"],
        ["Supplied as", pick.sys ? "Complete system with battery" : (pick.kit ? "With solar panels" : "Pump only")]
      ],
      includes:[
        "Brushless " + pick.hp + " HP pump with matched controller",
        pick.kind === "sub" ? "Submersible cable and splice kit" : "Suction and discharge fittings",
        pick.kit ? "Solar panels sized to run it through the day" : "Pump only — panels chosen separately",
        "Installation manual with pre-start checklist (PDF)"
      ],
      alt: (other && other.name !== pick.name)
        ? (function(){
            var d = other.price - pick.price;
            var gap = Math.abs(d) < 0.01 ? "for the same money"
                    : (d > 0 ? money(d) + " more" : money(-d) + " less");
            return pick.kind === "trans"
              ? {t:"The submersible option, for comparison",
                 b:"A submersible in the same form is " + esc(other.name) + " at " + money(other.price) + ", " + gap +
                   ". It sits in the water, so it needs no priming, cannot lose its prime, will not freeze on the bank and is out of sight. The surface pump above is easier to service — you can reach it without pulling anything out of a creek."}
              : {t:"The surface option, for comparison",
                 b:"A surface pump in the same form is " + esc(other.name) + " at " + money(other.price) + ", " + gap +
                   ". It stands on the bank, so it must be primed, kept above the flood line and protected from frost — but you can service it without lifting anything out of the water. The submersible above asks less of you."};
          })()

        : (function(){
            var checks = {
              well:  {t:"Check the standing water level before you order",
                      b:"This is the figure people get wrong. It is not the drilled depth, and it falls in late summer — exactly when you need the water most. Send us the driller’s log or a tape measurement and we will confirm the head before anything ships."},
              lake:  {t:"Measure at the low water mark, not today’s",
                      b:"A pond sized in spring is a different pump from the same pond in August. Give us the level at its lowest and how far the shoreline moves, and we will check this holds through the dry months rather than only the easy ones."},
              stream:{t:"Two levels matter here, not one",
                      b:"Size the lift from low water, but site the pump above high water. Tell us both, plus whether the creek runs year-round, and we will confirm the pump reaches in the dry season and survives the wet one."},
              tank:  {t:"Tell us whether the source tank draws down",
                      b:"If the tank you are pumping from empties as you go, the lift grows as it does, and the last of the transfer is the slowest part. Give us the tank height and how far it drops and we will size against the worst case rather than the first minute."}
            };
            return checks[a.source] || checks.well;
          })()
    };
  }

  /* ---------------------------------------------------------------
     System sizing — a requirement in numbers, then a scan.

     The old version of this function named products directly:
        if(where === "farm") pick = huge ? HYBRID.h20 : HYBRID.h10;
     which meant a system added to the store was invisible to the finder
     until somebody edited this file. That is the fault that hid the new
     hybrids when they were added in August.

     It now works the way the pump engine already did. The answers are
     turned into a duty — continuous kW, storage kWh, whether split-phase
     output is required — and then the catalog is searched for the smallest
     system that meets it. Nothing here knows a product name.

     PEAK is the largest simultaneous draw the building has to carry, and
     DAILY is what it gets through in 24 hours. SURGE is the motor start-up
     allowance: a farm's well pump, compressor and welder all draw several
     times their running current for the first second, and an inverter that
     cannot swallow that stalls every time something kicks in. SCALE adjusts
     daily consumption for how the building is actually occupied — a cottage
     used at weekends gets through far less than the same appliance list in
     a year-round house.
     --------------------------------------------------------------- */
  var LOAD_LEVEL = {
    light: {peak:2.0, daily:5,  says:"lights, a fridge and the basics"},
    mid:   {peak:3.5, daily:10, says:"normal daily living — fridge, lights, water pump, TV"},
    heavy: {peak:6.0, daily:18, says:"most of the building, including a well pump and laundry"},
    max:   {peak:9.0, daily:30, says:"everything, including air conditioning or a heat pump"}
  };
  var WHERE_PROFILE = {
    cottage: {surge:1.00, scale:0.6, split:false, says:"a seasonal property"},
    home:    {surge:1.15, scale:1.0, split:true,  says:"a year-round house"},
    farm:    {surge:1.60, scale:1.3, split:true,  says:"a farm or workshop"}
  };
  /* Off-grid carries the whole load overnight; a hybrid only carries the
     essential-circuits sub-panel, which is roughly this fraction of the house. */
  var ESSENTIAL_FRACTION = 0.4;

  function requirement(a){
    var L = LOAD_LEVEL[a.load] || LOAD_LEVEL.mid;
    var W = WHERE_PROFILE[a.where] || WHERE_PROFILE.home;
    var kw    = Math.round(L.peak * W.surge * 100) / 100;
    var daily = Math.round(L.daily * W.scale * 10) / 10;
    return {kw:kw, daily:daily, split:W.split, load:L, where:W};
  }

  function resolveSystem(a){
    /* Commercial buildings do not get a kit recommendation.
       Three-phase service, an interconnection agreement, a utility study, permits
       and often a stamped drawing sit between a commercial site and a working array.
       Handing that customer a 30 kW box is a worse answer than asking four questions
       and coming back with a real design, and it is the answer a competitor who does
       commercial work properly would give. */
    if(a.where === "comm"){
      return {project:true, goal:a.grid, load:a.load};
    }

    var need = requirement(a);
    var duty = "Sized on " + need.kw + " kW of continuous output" +
               (need.split ? " with split-phase 120/240 V, so standard household circuits and 240 V appliances run normally" : "") +
               ". That is " + need.load.peak + " kW of simultaneous load for " + need.where.says +
               (need.where.surge > 1 ? ", plus a " + Math.round((need.where.surge - 1) * 100) + "% allowance for motor start-up surge" : "") + ".";

    /* --- Grid, no battery: cutting the bill only. --- */
    if(a.grid === "bill"){
      var g = chooseSystem("gridtie", {kw:need.kw, split:need.split});
      if(!g.sys) return {project:true, goal:a.grid, load:a.load};
      var why = "You only want to reduce what you buy from the utility, so this has no battery — and that's most of why it costs roughly half what a backup system of the same size does. The trade-off is honest: when the grid goes down, this goes down with it. " + duty;
      if(g.short){
        why = "You only want to reduce what you buy from the utility, so this has no battery. " +
              "Be aware this is the largest grid-tie system we ship as one package, and it is below the " + need.kw +
              " kW your answers point to — past this size it becomes a designed project rather than a kit, and we would want an engineer on it before you order.";
      }
      return pack(g.sys, why, "gridtie", hybridPeer(g.sys), g.short);
    }

    /* --- Grid plus battery: a hybrid. --- */
    if(a.grid === "backup"){
      var storage = Math.round(need.daily * ESSENTIAL_FRACTION * 10) / 10;
      var h = chooseSystem("hybrid", {kw:need.kw, kwh:storage, split:need.split});
      if(!h.sys) return {project:true, goal:a.grid, load:a.load};
      var whyH = "You are staying connected to the utility but want the building to survive an outage, so this is a hybrid — it charges from the grid and the sun, stores energy, then takes over the instant the grid drops. No fuel, no start-up delay. " +
                 duty + " The battery is sized to carry your essential circuits — about " + storage +
                 " kWh, or a night of the " + Math.round(need.daily) + " kWh a day your answers suggest — and it is what you are paying for over a plain grid-tie system.";
      if(h.short){
        whyH = "This is the largest hybrid we ship as one package, and it is short of the " + need.kw +
               " kW and " + storage + " kWh your answers point to. It will run your essential circuits well; it will not run the whole building through a long outage. Worth a conversation before you order.";
      }
      return pack(h.sys, whyH, "hybrid", null, h.short);
    }

    /* --- No grid at all: battery mandatory, output must be split-phase for
           any year-round dwelling or working site. --- */
    var o = chooseSystem("offgrid", {kw:need.kw, split:need.split});
    if(!o.sys) return {project:true, goal:a.grid, load:a.load};
    var whyO = "With no utility at the property the system has to generate and store everything you use, so the battery is not optional. " + duty;
    if(a.where === "cottage"){
      whyO = "Seasonal use means you can size to typical days rather than worst-case winter. " + duty +
             " That covers " + need.load.says + " while you are there, with battery storage for nights and overcast spells.";
    } else if(a.where === "farm"){
      whyO = "Farm and workshop loads are dominated by motor start-up surge — well pumps, compressors, welders. " + duty +
             " That surge headroom is the difference between a system that runs and one that stalls every time something kicks in.";
    }
    if(o.short){
      whyO = "This is the largest complete off-grid system we ship as one package, and it is below the " + need.kw +
             " kW your answers point to. Past this size it stops being a kit and becomes a designed project — we would want an engineer on it before you order.";
    }
    return pack(o.sys, whyO, "offgrid", null, o.short);
  }

  /* For any grid-tie recommendation, the hybrid nearest the same inverter size —
     so the "what if you want outage protection too" alternative is a real system
     from the current range rather than a generic "starts at" figure. */
  function hybridPeer(sys){
    var best = null, gap = Infinity;
    catalogFamily("hybrid").forEach(function(h){
      if(h.kwh == null || h.pvKw == null) return;   /* needs both to be comparable */
      var d = Math.abs(h.kw - sys.kw);
      if(d < gap || (d === gap && best && h.price < best.price)){ gap = d; best = h; }
    });
    return best;
  }

  /* The design service used to be offered here, inside the recommendation, directly
     under Add to cart. It was removed deliberately.
     Two things were wrong with it. It contradicted this page's own promise — the whole
     finder says "answer three questions and we will tell you exactly what to buy", and
     then a paid upsell immediately said that answer had not seen your roof. And it put
     a second decision into a moment that should carry one. Most people buying these
     kits are self-installing with their own electrician; a design fee answers a
     question they did not ask.
     It now lives in the support section and on its own page, where someone who wants
     it will find it and nobody else is interrupted. */

  function pack(pick, why, kind, peer, short){
    var config = (kind === "backup" || kind === "hybrid") ? "Grid-tied hybrid, battery included"
               : kind === "gridtie" ? "Grid-tied, no battery"
               : "Off-grid, battery included";
    var includes;
    if(kind === "hybrid"){
      includes = ["Hybrid inverter — runs on grid, solar and battery",
                  kwhLabel(pick.kwh) + " of storage to carry your essential circuits through an outage",
                  kwLabel(pick.pvKw) + " of bifacial solar panels","Cable and protection devices"];
      if(!pick.noDocs) includes.push("Installation manual with pre-start checklist (PDF)");
    } else if(kind === "gridtie"){
      includes = ["Solar panels sized to cut your bill","Grid-tie inverter matched to the array","Cable and protection devices","Installation manual with pre-start checklist (PDF)","No battery — this system stops when the grid does"];
    } else if(kind === "backup"){
      includes = ["Hybrid inverter with automatic transfer switch","Battery storage to carry your essential circuits through an outage","Solar panels","Monitoring and app control","Installation manual with pre-start checklist (PDF)"];
    } else {
      includes = ["Solar panels sized to the load","Charge controller and inverter, matched to the array","Battery bank for overnight and overcast running","Cable and protection devices","Installation manual with pre-start checklist (PDF)"];
    }
    var alt;
    if(kind === "hybrid"){
      alt = pick.alt2
        ? (function(){
            var a2 = pick.alt2, dK = (a2.kwh || 0) - (pick.kwh || 0), dP = a2.price - pick.price;
            if(dK < 0 && dP > 0){
              /* Smaller battery, higher price. Not an error: the two options are different
                 battery brands at different costs per kWh — GSL is about $230/kWh in the
                 catalogue, Growatt about $340/kWh — so a smaller Growatt pack really does
                 cost more to supply than a larger GSL one. The customer still needs that
                 said out loud, or the row looks like a mistake. */
              return {t:"The second battery option is smaller and costs more. Here is why",
                      b:"The same system is also listed with "+a2.label.replace(" instead","")+" at "+money(a2.price)+" — "+
                        Math.abs(dK).toFixed(2)+" kWh <strong>less</strong> storage for "+money(dP)+" <strong>more</strong>. "+
                        "That is not a mistake: the two packs are different brands at different costs per kWh, and the smaller one is the dearer chemistry. "+
                        "It arrives as 5 kWh modules rather than single large units, so it is easier to carry, easier to site and easier to extend later. "+
                        "If none of that matters to you, take the "+kwhLabel(pick.kwh)+" option — it is more storage for less money."};
            }
            var per = dK > 0 ? Math.round(dP/dK) : null;
            return {t:"Need more hours of storage?",
                    b:"The same system with "+a2.label.replace(" instead","")+" is "+money(a2.price)+" — "+money(dP)+" more for "+
                      dK.toFixed(2)+" kWh extra"+(per !== null ? ", about $"+per.toLocaleString("en-US")+" per kWh" : "")+". "+
                      "Same panels, same inverter; the difference is how long it runs your backed-up circuits after dark."};
          })()
        : (function(){
            /* No second battery option on this model, so offer the cheaper hybrids
               below it — read from the catalog, not named here, so this stays
               correct as the range changes. */
            var below = catalogFamily("hybrid").filter(function(h){
              return h.kwh != null && h.price < pick.price;
            }).sort(function(x,y){ return x.price - y.price; }).slice(0, 2);
            if(!below.length){
              return {t:"This is the smallest hybrid we make",
                      b:"There is nothing below it in the range. If the price is the problem, a grid-tie system cuts the same amount off your bill for roughly half the money — you lose outage protection and nothing else."};
            }
            return {t:"Want the same protection for less?",
                    b:below.map(function(h){
                        return "The "+esc(h.name.split(" — ")[0])+" is "+money(h.price)+" with "+kwhLabel(h.kwh)+" of storage";
                      }).join(", and ")+
                      ". "+(below.length > 1 ? "Both back" : "It backs")+" up an essential-circuits sub-panel the same way — you get less panel area and fewer hours of storage for the money."};
          })();
    } else if(kind === "gridtie"){
      alt = peer
        ? {t:"Want it to work during an outage too?",
           b:"The "+esc(peer.name.split(" — ")[0])+" is "+money(peer.price)+" — "+money(peer.price - pick.price)+" more than this. "+
             "It carries "+kwLabel(peer.pvKw)+" of panels and "+kwhLabel(peer.kwh)+" of storage, so you get the same bill saving plus circuits that stay live when the grid drops. "+
             (peer.kw < pick.kw
               ? "Its inverter is "+kwLabel(peer.kw)+" against this system's "+kwLabel(pick.kw)+", so peak output is a little lower — the battery is what you are buying. "
               : "That gap is almost entirely the battery. ")+
             "Only worth it if outages actually bother you — if they don't, don't pay for it."}
        : {t:"Want it to work during an outage too?",
           b:"Then you need a battery, and the price roughly doubles. Only worth it if outages actually bother you."};
    } else if(kind === "backup"){
      var cheapGT = catalogFamily("gridtie").sort(function(x,y){ return x.price - y.price; })[0];
      alt = cheapGT
        ? {t:"Don't care about outages?", b:"A grid-tie system with no battery saves the same amount on your bill from "+money(cheapGT.price)+". You lose outage protection and nothing else."}
        : {t:"Don't care about outages?", b:"A grid-tie system with no battery saves the same amount on your bill for roughly half the money. You lose outage protection and nothing else."};
    } else {
      alt = {t:"Want this checked against your real numbers?", b:"Send us a power bill or a list of what has to run. We'll confirm the size before you order — it costs nothing and it's the step most people skip."};
    }
    return {
      save:{mode: kind === "gridtie" ? "gridHome"
                 : ((kind === "backup" || kind === "hybrid") ? "gridBackup" : "offgrid"),
            /* generation follows the array where the array is published, otherwise
               the inverter — a system with no pvKw can still be financed, just less
               precisely, and the calculator says which figure it used. */
            kw: (pick.pvKw != null ? pick.pvKw : pick.kw), cost: pick.price},
      because: why, name: pick.name, price: pick.price, stock: pick.stock,
      short: !!short, variantId: pick.variantId, url: pick.url,
      image: pick.image, docs: pick.docs,
      /* The catalog row itself travels with the result, so the add-on step can
         check parts against the real system rather than against its name. */
      sys: pick,
      specs: pick.pvKw != null
        ? [["Inverter size", kwLabel(pick.kw)],["Solar array", kwLabel(pick.pvKw)],["Battery storage", kwhLabel(pick.kwh)],["Configuration", config],["Backup scope","Partial home — essential circuits sub-panel"],["Supplied as","Complete system"]]
        : [["System size", kwLabel(pick.kw)],["Output", pick.volt],["Configuration", config],["Supplied as","Complete system"]],
      includes: includes, alt: alt,
      hasDocs: pick.noDocs ? false : true
    };
  }

  var RESOLVE = {pool:resolvePool, well:resolveWell, system:resolveSystem};

  /* ---- Engine ---- */
  var state = {track:null, step:0, answers:{}, pumpBuy:null};

  function esc(s){ return String(s).replace(/[&<>"]/g, function(c){ return {"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;"}[c]; }); }

  function activeSteps(){
    var f = FLOWS[state.track];
    var out = [];
    for(var i=0;i<f.steps.length;i++){
      var s = f.steps[i];
      if(typeof s.when === "function" && !s.when(state.answers)) continue;
      out.push(s);
    }
    return out;
  }

  function renderCrumb(){
    if(!state.track){ crumb.innerHTML = ""; return; }
    var f = FLOWS[state.track];
    var total = activeSteps().length;
    var at = Math.min(state.step + 1, total);
    var done = state.step >= total;
    crumb.innerHTML = "<b>"+esc(f.label)+"</b> · " + (done ? "Recommendation" : ("Question "+at+" of "+total));
  }

  function renderStart(){
    stage.textContent = "Choose what you're building";
    renderCrumb();
    body.innerHTML =
      '<p class="sel-q">What are you trying to do?</p>'+
      '<p class="sel-help">Pick the one that sounds like your situation. You can change your mind at any point.</p>'+
      '<div class="opts">'+
        '<button class="opt" type="button" data-start="system"><b>Power a building</b><span>A home, cottage, farm or commercial site — off grid, grid-tied, or with battery backup</span></button>'+
        '<button class="opt" type="button" data-start="well"><b>Pump water</b><span>A well, borehole, pond or tank — for a house, livestock or irrigation</span></button>'+
        '<button class="opt" type="button" data-start="pool"><b>Run a swimming pool</b><span>Replace a mains pool pump with a solar one</span></button>'+
      '</div>';
  }

  function renderStep(){
    var f = FLOWS[state.track];
    var steps = activeSteps();
    if(state.step >= steps.length){ return renderResult(); }
    var s = steps[state.step];
    stage.textContent = f.label;
    renderCrumb();
    /* help and hint carry authored inline markup, so they are trusted and not escaped.
       Either may be a function of the answers so far — a question about lift means
       something different at a borehole than at a creek, and the guidance has to follow. */
    var helpTxt = (typeof s.help === "function") ? s.help(state.answers) : s.help;
    var qTxt    = (typeof s.q    === "function") ? s.q(state.answers)    : s.q;
    var html = '<p class="sel-q">'+esc(qTxt)+'</p><p class="sel-help">'+helpTxt+'</p>';

    if(s.type === "num"){
      var start = (state.answers[s.id] !== undefined) ? state.answers[s.id] : s.val;
      html += '<div class="numwrap">'+
                '<div class="numval"><b id="numOut">'+start.toLocaleString("en-US")+'</b><span>'+esc(s.unit)+'</span></div>'+
                '<input type="range" id="numIn" min="'+s.min+'" max="'+s.max+'" step="'+s.step+'" value="'+start+'" '+
                  'aria-label="'+esc(qTxt)+'">'+
                '<div class="numscale"><span>'+s.min.toLocaleString("en-US")+'</span>'+
                  '<span>'+s.max.toLocaleString("en-US")+' '+esc(s.unit)+'</span></div>'+
                '<p class="numhint" id="numHint">'+esc(s.hint(start, state.answers))+'</p>'+
              '</div>'+
              '<div class="numacts">'+
                '<button class="btn btn-amber" type="button" data-num="1">Continue</button>'+
                '<button class="sel-back" type="button" data-back="1">← Back</button>'+
              '</div>';
      body.innerHTML = html;
      return;
    }

    html += '<div class="opts">';
    for(var i=0;i<s.options.length;i++){
      var o = s.options[i];
      html += '<button class="opt" type="button" data-answer="'+esc(o.v)+'"><b>'+esc(o.l)+'</b><span>'+esc(o.s)+'</span></button>';
    }
    html += '</div><button class="sel-back" type="button" data-back="1">← Back</button>';
    body.innerHTML = html;
  }

  /* ---- Commercial: an intake form, not a product ---- */
  var GOAL_LABEL = {
    bill:   "Cut the electricity bill",
    backup: "Cut the bill and keep critical circuits running through an outage",
    off:    "Run independently of the utility"
  };
  var LOAD_LABEL = {
    light: "Essential circuits only", mid: "Normal daily operation",
    heavy: "Most of the building", max: "Everything, including HVAC or process load"
  };

  /* Every control is named contact[...] because the form posts to Shopify's own
     contact endpoint, which emails the shop every contact[] field it is given.
     No app, no third-party form service, nothing to maintain. `req` marks the
     one field the endpoint actually needs. */
  function field(id, label, hint, type, opts, req){
    var nm = ' name="contact[' + id + ']"', rq = req ? ' required' : '';
    var input;
    if(type === "select"){
      input = '<select id="'+id+'"'+nm+'>'+opts.map(function(o){ return '<option>'+esc(o)+'</option>'; }).join("")+'</select>';
    } else if(type === "area"){
      input = '<textarea id="'+id+'"'+nm+' rows="3" placeholder="'+esc(hint||"")+'"></textarea>';
    } else {
      input = '<input id="'+id+'"'+nm+rq+' type="'+(type||"text")+'" placeholder="'+esc(hint||"")+'">';
    }
    return '<label class="fld" for="'+id+'"><span class="fld-l">'+label+(req?' <em>required</em>':'')+'</span>'+input+'</label>';
  }

  function renderProject(r){
    var goal = GOAL_LABEL[r.goal] || GOAL_LABEL.bill;
    var load = LOAD_LABEL[r.load] || "";

    body.innerHTML =
      '<div class="proj">' +
        '<div class="proj-lede">' +
          '<div class="res-because"><b>Why we are not showing you a price</b>' +
            'A commercial building is not a bigger house. Three-phase service, an interconnection agreement with your utility, a demand charge that often costs more than the energy itself, permits and in most states a stamped drawing all sit between you and a working array — and every one of them changes the equipment list. Anyone who quotes you off a web form without seeing a bill is guessing, and you will pay for the guess at commissioning.</div>' +
          '<h3>Tell us about the building. We will design the system.</h3>' +
          '<p class="proj-sub">Four minutes of your time gets you a single-line diagram, an equipment list, an array layout and a production estimate run against your own tariff — not a catalogue number. No cost, no obligation.</p>' +
          '<div class="proj-echo"><span class="eyebrow">From your answers</span>' +
            '<div class="spec-row"><span>Property</span><span>Commercial building</span></div>' +
            '<div class="spec-row"><span>Goal</span><span>' + esc(goal) + '</span></div>' +
            (load ? '<div class="spec-row"><span>Load</span><span>' + esc(load) + '</span></div>' : '') +
          '</div>' +
        '</div>' +

        '<form class="proj-form" method="post" action="" accept-charset="UTF-8">' +
          '<input type="hidden" name="form_type" value="contact">' +
          '<input type="hidden" name="utf8" value="✓">' +

          '<input type="hidden" name="contact[From the finder]" value="' +
            esc('Commercial building · ' + goal + (load ? ' · ' + load : '')) + '">' +

          '<fieldset class="fs"><legend><span class="fs-n">1</span>Your electricity</legend>' +
            '<p class="fs-help">This decides the design. Commercial tariffs bill <b>demand</b> — your highest 15-minute peak — separately from energy, and on many sites the demand charge is the larger number. A system sized only against kWh can miss most of the saving.</p>' +
            '<div class="file-drop"><b>Send us 12 months of utility bills</b>' +
              '<span>Reply to the confirmation email with them attached, or email them over separately. ' +
              'PDF, image or spreadsheet. This one thing does more for the quote than every other field on this form.</span></div>' +
            '<div class="fld-row">' +
              field("kwh","Annual consumption <em>if you know it</em>","e.g. 180,000 kWh") +
              field("peak","Peak demand <em>if it is on your bill</em>","e.g. 95 kW") +
            '</div>' +
            field("util","Utility and rate schedule","e.g. Oncor, Secondary Voltage 1-10 kV") +
          '</fieldset>' +

          '<fieldset class="fs"><legend><span class="fs-n">2</span>The building</legend>' +
            '<p class="fs-help">Service voltage and phase decide which inverters can be used at all. If you are not sure, photograph the label on your main panel and send it with the bills — that is faster than guessing.</p>' +
            '<div class="fld-row">' +
              field("btype","Building type","","select",["Warehouse or distribution","Office","Retail","Light industrial or workshop","Agricultural or barn","Cold storage","Other"]) +
              field("svc","Electrical service","","select",["208 V three-phase","240 V single-phase","480 V three-phase","600 V three-phase","Not sure — I will send a photo"]) +
            '</div>' +
            '<div class="fld-row">' +
              field("main","Main breaker rating","e.g. 400 A") +
              field("mount","Where the array goes","","select",["Roof","Ground mount","Carport or canopy","Roof and ground","Not decided"]) +
            '</div>' +
            field("roof","Roof or ground detail","Roof type and approximate age, or how much clear ground you have. A 20-year-old membrane roof usually wants replacing before an array goes on it — better to know now.","area") +
          '</fieldset>' +

          '<fieldset class="fs"><legend><span class="fs-n">3</span>What has to keep running</legend>' +
            '<p class="fs-help">Only if you want outage cover. Backup is sized from the circuits that must survive, not from the size of the building — naming them is what keeps the battery affordable.</p>' +
            field("crit","Critical loads","e.g. walk-in freezers, two dock doors, office lighting, the server rack. Or: nothing — we just want the bill down.","area") +
          '</fieldset>' +

          '<fieldset class="fs"><legend><span class="fs-n">4</span>How you want to proceed</legend>' +
            '<div class="fld-row">' +
              field("when","Timeline","","select",["Ready to order","Within 3 months","6 to 12 months","Budgeting for next year"]) +
              field("who","Installation","","select",["We have our own electrician or EPC","We need a referral","Not decided yet"]) +
            '</div>' +
            '<div class="fld-row">' +
              field("co","Company","","text") +
              field("name","Your name","","text") +
            '</div>' +
            '<div class="fld-row">' +
              field("email","Email","you@company.com","email",null,true) +
              field("phone","Phone","Optional, but it usually saves a week","tel") +
            '</div>' +
            field("site","Site address or city and state","We need the location for sun yield, utility rules and which incentives apply","text") +
          '</fieldset>' +

          '<div class="proj-acts">' +
            '<button class="btn btn-amber" type="submit">Send this to the engineering desk</button>' +
            '<span class="proj-note">A specialist replies within <b>two business days</b>. If your answers show the site needs a licensed designer rather than us, we will say so.</span>' +
          '</div>' +
        '</form>' +

        '<div class="proj-back">' +
          '<div class="proj-deliver">' +
            '<span class="eyebrow">What comes back</span>' +
            '<ul class="incl">' +
              '<li><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M4 10.5l4 4 8-9"/></svg><span><b>A single-line diagram</b> of the proposed system, with ratings, that your electrician can quote from.</span></li>' +
              '<li><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M4 10.5l4 4 8-9"/></svg><span><b>An equipment list</b> priced line by line, with lead times, so you can see what you are buying rather than a lump sum.</span></li>' +
              '<li><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M4 10.5l4 4 8-9"/></svg><span><b>An array layout</b> on your roof or ground area, with the string configuration checked against the inverter limits.</span></li>' +
              '<li><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M4 10.5l4 4 8-9"/></svg><span><b>A production and payback estimate</b> run against your actual tariff and demand profile — including the demand charge, which generic calculators ignore.</span></li>' +
              '<li class="excl"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M5 5l10 10M15 5L5 15"/></svg><span><b>Not included — stamped drawings, permits or interconnection filing.</b> Those need a licensed engineer in your state. We will tell you which of them your project needs and roughly what they cost, so nothing arrives as a surprise.</span></li>' +
            '</ul>' +
          '</div>' +
          '<div class="proj-alt">' +
            '<h4>Already have a design?</h4>' +
            '<p>If an engineer has specified the system and you only need the equipment, skip all of this. Our commercial three-phase inverters run to 150 kW and the catalogue prices every line.</p>' +
            '<a class="btn" href="https://rocksolar.io/collections/commercial-inverters">Commercial inverters →</a>' +
            '<h4 style="margin-top:1.6rem">Is your site actually small?</h4>' +
            '<p>A single-phase workshop or a small retail unit may well be served by a standard system after all. Say so in the form, or start over and answer as a farm or workshop.</p>' +
            '<button class="sel-back" type="button" data-restart="1">← Start over</button>' +
          '</div>' +
        '</div>' +
      '</div>';

    lastResult = null;
  }

  function renderResult(){
    var f = FLOWS[state.track];
    var r = RESOLVE[state.track](state.answers);
    renderCrumb();
    if(r.project){ stage.textContent = f.label + " — commercial project"; renderProject(r); return; }
    if(r.blocked){
      stage.textContent = f.label + " — not something we sell";
      body.innerHTML =
        '<div class="res-because" style="border-left-color:var(--alert,#A6231C);background:var(--alert-wash,#FBECEA)">'+
          '<b>'+esc(r.title)+'</b>'+r.because+'</div>'+
        '<div class="res-alt" style="margin-top:1.5rem;max-width:46rem"><h4>'+esc(r.alt.t)+'</h4><p>'+r.alt.b+'</p></div>'+
        '<div class="res-acts" style="margin-top:1.5rem">'+
          '<a class="btn btn-amber" href="' + L_SUPPORT + '">Ask us what to shop for</a>'+
          '<button class="sel-back" type="button" data-restart="1">← Start over</button>'+
        '</div>';
      lastResult = null; return;
    }
    if(r.nofit){
      stage.textContent = f.label + " — no pump reaches this";
      body.innerHTML =
        '<div class="res-because"><b>Nothing in the range does this</b>'+r.because+'</div>'+
        r.head +
        '<div class="res-alt" style="margin-top:1.5rem;max-width:46rem"><h4>'+esc(r.alt.t)+'</h4><p>'+esc(r.alt.b)+'</p></div>'+
        '<div class="res-acts" style="margin-top:1.5rem">'+
          '<a class="btn btn-amber" href="' + L_SUPPORT + '">Talk it through with us</a>'+
          '<button class="sel-back" type="button" data-restart="1">← Start over</button>'+
        '</div>';
      lastResult = null; return;
    }
    stage.textContent = f.label + " — recommended";

    var incl = "";
    for(var i=0;i<r.includes.length;i++){
      incl += '<li><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M4 10.5l4 4 8-9"/></svg><span>'+esc(r.includes[i])+'</span></li>';
    }
    incl += '<li class="excl"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15"/></svg>'+
            '<span><b>Not included — racking and mounting.</b> Roof and ground mounts depend on your roof type, pitch and local wind and snow loading, so we don\'t guess at it. '+
            '<a href="' + L_CATALOG + '">Price your mounting →</a> — the catalog prices roof, ground and wall racking against your panel count. Or tell us your roof and we\'ll specify it with you.</span></li>';
    var specs = "";
    for(var j=0;j<r.specs.length;j++){
      specs += '<div class="spec-row"><span>'+esc(r.specs[j][0])+'</span><span>'+esc(r.specs[j][1])+'</span></div>';
    }

    body.innerHTML =
      '<div class="res">'+
        '<div>'+
          (r.siteFix || '')+
          '<div class="res-because"><b>Why this one</b>'+r.because+'</div>'+
          '<h3>'+esc(r.name)+'</h3>'+
          (r.buy && r.buy.length > 1
            ? '<div class="buyopts" role="group" aria-label="How to buy it">'+
              r.buy.map(function(b){
                var on = b.id === r.buyId;
                return '<button type="button" class="buyopt'+(on?' on':'')+'" data-pbuy="'+b.id+'" aria-pressed="'+on+'">'+
                       '<span class="buyopt-l">'+esc(b.label)+'</span>'+
                       '<span class="buyopt-p">'+money(b.row.price)+'</span>'+
                       '<span class="buyopt-s">'+esc(b.sub)+'</span>'+
                       (b.row.stock>0
                         ? '<span class="buyopt-k"><span class="dot"></span>'+b.row.stock.toLocaleString("en-US")+' in stock</span>'
                         : '<span class="buyopt-k oos">Out of stock</span>')+
                       '</button>';
              }).join("")+
              '</div>'
            : '')+
          '<div class="res-price">'+money(r.price)+'</div>'+
          '<span class="res-stock"><span class="dot"></span>'+r.stock.toLocaleString("en-US")+' in stock, ships now</span>'+
          '<ul class="incl">'+incl+'</ul>'+
          '<div class="res-acts">'+
            '<a class="btn btn-amber" href="'+esc(cartLink([[r.variantId,1]], r.url))+'">'+
              (r.stock > 0 ? 'Add to cart' : 'See it on the product page')+'</a>'+
            '<a class="btn" href="' + L_SUPPORT + '">Have this checked first</a>'+
          '</div>'+
          (globalThis.RS_SAVE ?
          '<button class="save-cue" type="button" data-scroll-save="1">'+
            '<span class="save-cue-txt">This system saves you <b id="cueAmt">—</b> a year in <span id="cueState">Texas</span>'+
              '<em id="cuePay"></em></span>'+
            '<span class="save-cue-arrow" aria-hidden="true">'+
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">'+
                '<path d="M12 4v15M5.5 12.5L12 19l6.5-6.5"/></svg>'+
            '</span>'+
          '</button>' : '')+
          '<button class="sel-back" type="button" data-restart="1">← Start over</button>'+
        '</div>'+
        '<div>'+
          (r.image
            ? '<a class="res-shot" href="'+esc(r.url || '#')+'">'+
                '<img src="'+esc(r.image)+'" alt="'+esc(r.name)+'" loading="lazy">'+
              '</a>'
            : '<div class="shot shot-sq"><span class="shot-label"><b>NO PHOTO</b>'+
              'This listing has no image yet</span></div>')+
          '<div class="spec-rows">'+specs+'</div>'+
          (r.head || '')+
          '<div class="res-alt" style="margin-top:1.5rem"><h4>'+esc(r.alt.t)+'</h4><p>'+r.alt.b+'</p></div>'+
        '</div>'+
      '</div>'+
      (r.others && r.others.length
        ? '<div class="alsofit"><span class="eyebrow">'+r.hitCount+' models reach this head — here are the next three</span>'+
          '<div class="alsofit-rows">'+
          r.others.map(function(o){
            return '<div class="alsofit-row"><div><b>'+esc(o.name)+(o.model!=="-"?' — '+esc(o.model):'')+'</b>'+
                   '<span>'+o.hp+' HP · '+o.qAt.toFixed(1)+' GPM at this head · '+
                   (o.kit?'with panels':(o.sys?'complete system':'pump only'))+'</span></div>'+
                   '<div class="alsofit-p">'+money(o.price)+
                   (o.stock>0?'<span class="res-stock"><span class="dot"></span>in stock</span>':'<span class="oos">out of stock</span>')+
                   '</div></div>';
          }).join("")+
          '</div></div>'
        : '')+
      '<div id="resAddons"></div>'+
      '<div id="resDocs"></div>'+
      '<div id="resSave"></div>';

    lastResult = r;
    renderAddons();
    renderDocs();
    renderSavings();
  }


  /* ---------------------------------------------------------------
     ADD TO THE SYSTEM

     Everything here is filtered against the system that was just
     recommended. The rule throughout: if a part cannot work with that
     system, it is not offered — and the page says why rather than
     silently hiding it. A customer who is told "you cannot add a
     battery to a grid-tie inverter, here is the hybrid that does it"
     has learned something; one shown a battery that will not connect
     has been sold a return.
     --------------------------------------------------------------- */
  var addon = null;
  function resetAddons(){ addon = {rackId:null}; }
  resetAddons();

  /* How many panels the kit carries, so racking is priced for the whole array.
     The kits do not publish a panel count, so it is derived from array kW at a
     nominal 450 W module — and labelled as an estimate everywhere it is shown,
     because the customer will be buying rails against it. */
  function addonBasePanels(sys){
    if(sys.pvKw == null) return null;
    return Math.round(sys.pvKw * 1000 / 450);
  }

  function renderAddons(){
    var host = document.getElementById("resAddons");
    if(!host || !lastResult || !lastResult.sys) return;
    var sys  = lastResult.sys;
    var base = addonBasePanels(sys);

    var body, total = 0, chosen = null, chosenFit = null;
    if(base == null){
      body = '<p class="addon-none">Racking is priced by the number of panels, and this system does not publish its ' +
        'array size. Tell us the panel count and the roof it is going on and we will price it exactly. ' +
        '<a href="' + L_CATALOG + '">See the range →</a></p>';
    } else {
      if(addon.rackId != null){
        chosen    = RACKING[addon.rackId];
        chosenFit = rackFor(chosen, base);
        if(chosenFit && !chosenFit.short) total = chosenFit.price;
      }
      body = '<p class="addon-w"><b>Not included in any kit</b>, and the part most likely to stop an install on the day. ' +
        'Roof and ground mounts depend on the roof type, the pitch and your local wind and snow loading, which is why ' +
        'we do not put a guess in the box — but you still need it, so here it is priced against your array.</p>' +
        '<p class="addon-w">About <b>' + base + ' panels</b>. The kits do not publish a panel count, so that is ' +
        kwLabel(sys.pvKw) + ' at a nominal 450 W module — <b>confirm it against what actually arrives</b> before ' +
        'you order rails, because a pack is priced by the panel.</p>' +
        '<div class="addon-pick">' + RACKING.map(function(r, i){
          var fit = rackFor(r, base);
          var on  = (addon.rackId === i);
          if(!fit) return '<button type="button" class="apick" disabled>' + esc(r.surface) +
                          '<small>over ' + r.packs[r.packs.length-1][0] + ' panels — ask us</small></button>';
          return '<button type="button" class="apick' + (on ? ' on' : '') + '" data-arack="' + i + '"' +
                 (fit.short ? ' disabled' : '') + '>' + esc(r.surface) +
                 '<small>' + money(fit.price) + ' · ' + esc(fit.label) +
                 (fit.short ? ' · out of stock' : ' · $' + (fit.price/base).toFixed(0) + '/panel') +
                 '</small></button>';
        }).join("") + '</div>';
      if(chosen && chosenFit){
        body += '<span class="addon-limit">' + esc(chosen.note) +
          (chosenFit.exact ? '' : ' — nearest pack is ' + chosenFit.label + ', for your ' + base + ' panels') +
          '</span>';
      }
    }

    host.innerHTML =
      '<div class="addons">' +
        '<div class="addons-head"><div><span class="eyebrow">The one part that is not in the box</span>' +
        '<p class="addons-lede">Everything else the system needs ships with it. Racking does not, because it depends ' +
        'on your roof — so it is the last thing to settle before you order.</p></div></div>' +
        '<div class="addon"><div class="addon-top"><div>' +
          '<div class="addon-t">Racking and mounting</div>' + body +
        '</div>' +
        (total > 0
          ? '<div class="addon-ctl"><div class="addon-sum">' + money(total) +
            '<span>' + esc(chosenFit.label) + '</span></div></div>'
          : '') +
        '</div></div>' +
        '<div class="addons-total"><div>' +
          '<span class="k">System ' + money(lastResult.price) +
            (total > 0 ? ' plus racking ' + money(total) : ' — racking not chosen') + '</span>' +
          '<div class="v">' + money(lastResult.price + total) + '</div>' +
          '<p class="brk">' + (total > 0
            ? esc(chosen.name) + ' — ' + esc(chosenFit.label) + ' — ' + money(total)
            : 'Pick the surface it is going on above, or add the system on its own and sort the mounting later.') +
          '</p></div>' +
          '<div class="save-cta-acts">' +
            '<a class="btn btn-amber" href="' +
              esc(cartLink(total > 0
                    ? [[lastResult.variantId, 1], [chosenFit.variantId, chosenFit.qty]]
                    : [[lastResult.variantId, 1]], lastResult.url)) + '">' +
              (total > 0 ? 'Add both to cart' : 'Add the system to cart') + '</a>' +
            (total > 0 ? '<button class="btn" type="button" data-aclear="1">Remove racking</button>' : '') +
          '</div>' +
        '</div>' +
        '<p class="addons-legal"><b>What this figure is</b>' +
        'A price against an estimated panel count, not a mounting design. The rail layout, the span between feet and ' +
        'the fixings into your roof structure depend on wind and snow loading where you are, and are the responsibility ' +
        'of the licensed installer. Send us the roof and we will check the pack before you order.</p>' +
      '</div>';
  }


  function renderDocs(){
    var host = document.getElementById("resDocs");
    if(!host || !lastResult) return;
    var urls = lastResult.docs || {};

    var items = "", have = 0;
    for(var i=0;i<DOCS.length;i++){
      var doc = DOCS[i], url = urls[doc.k];
      var icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">'+doc.i+'</svg>';
      var head = '<span class="docitem-t">'+esc(doc.t)+'</span>'+
                 '<span class="docitem-d">'+esc(doc.d)+'</span>';
      if(url){
        have++;
        items += '<a class="docitem" href="'+esc(url)+'" target="_blank" rel="noopener">'+
                 icon + head + '<span class="docitem-a">'+esc(doc.a || "Download PDF →")+'</span></a>';
      } else {
        items += '<div class="docitem docitem-soon">'+
                 icon + head + '<span class="docitem-a">In preparation</span></div>';
      }
    }

    /* Nothing published at all is worth saying outright rather than showing a
       grid of four greyed-out rows. */
    if(have === 0){
      host.innerHTML =
        '<div class="docs-block docs-pending">'+
          '<div class="docs-head"><span class="eyebrow">Documentation</span>'+
          '<p class="docs-lede">We have not published a document set for this one yet.</p></div>'+
          '<p class="docs-none">We would rather say so than hand you a file that does not exist. '+
          'Call us before you order and we will walk the connections through with you directly, '+
          'and send you what we do have. '+
          '<a href="' + L_SUPPORT + '">Talk to an engineer →</a></p>'+
        '</div>';
      return;
    }

    host.innerHTML =
      '<div class="docs-block">'+
        '<div class="docs-head">'+
          '<div><span class="eyebrow">Documentation</span>'+
          '<p class="docs-lede">Published before you buy, not shipped in the box. No email address required.</p></div>'+
          '<span class="docs-badge">'+have+' of '+DOCS.length+' published</span>'+
        '</div>'+
        '<div class="docs-grid">'+items+'</div>'+
        (have < DOCS.length
          ? '<p class="docs-none"><b>The rest are being written.</b> Where a document is not published yet we say so '+
            'rather than showing you a dead link. If you need one of them before you order, '+
            '<a href="' + L_SUPPORT + '">ask us</a> and we will send what we have.</p>'
          : '')+
        '<p class="docs-legal"><b>For customer reference only</b>'+
          'These documents show the intended topology and the equipment ratings of the kit as supplied. They are not approved installation drawings and not a design for your site. '+
          'Conductor sizing, overcurrent protection, grounding and bonding, and compliance with the NEC and local code are the responsibility of the licensed electrician or professional engineer who designs, installs and inspects your system. '+
          'Have them review these documents before you order materials or begin work. Where anything conflicts with the manufacturer manual, the manual takes precedence.</p>'+
      '</div>';
  }

  /* ---- What it saves, shown with the recommendation ---- */
  var lastResult = null;
  var savedState = "TX";

  function renderSavings(){
    var host = document.getElementById("resSave");
    if(!host || !lastResult || !lastResult.save || !globalThis.RS_SAVE) return;

    var API = globalThis.RS_SAVE;

    var st = null;
    for(var i=0;i<API.STATES.length;i++){ if(API.STATES[i][0] === savedState){ st = API.STATES[i]; break; } }
    if(!st) st = API.STATES[0];

    var sv = lastResult.save;
    var item = {cost: sv.cost, kw: sv.kw, kwh: sv.kwh};
    var res = API.compute(st, sv.mode, item);

    var opts = "";
    for(var k=0;k<API.STATES.length;k++){
      opts += '<option value="'+API.STATES[k][0]+'"'+(API.STATES[k][0]===savedState?' selected':'')+'>'+esc(API.STATES[k][1])+'</option>';
    }

    var inc = API.incentives(st, sv.mode, item, res), live = [];
    for(var m=0;m<inc.length;m++){ if(inc[m].tag === "yes" || inc[m].tag === "maybe") live.push(inc[m]); }
    var incHtml;
    if(live.length){
      incHtml = '<ul class="save-inc">';
      for(var n=0;n<Math.min(live.length,3);n++){
        incHtml += '<li><span class="inc-tag '+(live[n].tag==="yes"?"yes":"maybe")+'">'+esc(live[n].tag)+'</span><span>'+esc(live[n].title)+'</span></li>';
      }
      incHtml += '</ul>';
    } else {
      incHtml = '<p class="save-none">No state programme we know of applies to this purchase in '+esc(st[1])+'. The figure above is what it earns on its own.</p>';
    }

    var as = API.assumptions(st, sv.mode, item, res), asHtml = "";
    for(var q=0;q<as.length;q++){ asHtml += "<li>"+as[q]+"</li>"; }

    host.innerHTML =
      '<div class="save-block">'+
        '<div class="save-head">'+
          '<div><span class="eyebrow">And here is what it saves you</span>'+
          '<p class="save-lede">The same system, priced against what you are paying now.</p></div>'+
          '<label class="save-state">Where you are <select id="resState">'+opts+'</select></label>'+
        '</div>'+
        '<div class="save-stats">'+
          '<div class="stat lead"><span class="k">Saved per year</span><span class="v">'+API.usd(res.annual)+'</span><span class="sub">'+esc(res.basis)+'</span></div>'+
          '<div class="stat"><span class="k">Pays for itself in</span><span class="v">'+(res.payback>0?res.payback.toFixed(1)+' yrs':'—')+'</span><span class="sub">on '+API.usd(res.cost)+' of equipment</span></div>'+
          '<div class="stat"><span class="k">Over '+API.YEARS+' years</span><span class="v">'+API.usd(res.lifetime)+'</span><span class="sub">'+Math.round(res.kwh).toLocaleString('en-US')+' kWh a year '+(res.util?'offset':'replaced')+'</span></div>'+
        '</div>'+
        '<div class="save-foot">'+
          '<div><h4>Incentives worth checking in '+esc(st[1])+'</h4>'+incHtml+'</div>'+
          '<div class="save-fed"><b>No federal credit</b>The 30% federal residential credit ended 31 December 2025. Nothing above includes it.</div>'+
        '</div>'+
        '<details class="assump"><summary>Assumptions behind these numbers</summary><ul>'+asHtml+'</ul></details>'+
        '<p class="save-legal">An estimate for comparison, not a quote or a guarantee — it depends on orientation, shading, weather, your tariff and how you use power. Verify programmes at DSIREUSA.org.</p>'+
'<div class="save-cta">'+
      '<div class="save-cta-item">'+
        '<span class="eyebrow">Your recommended system</span>'+
        '<h4>'+esc(lastResult.name)+'</h4>'+
        '<div class="save-cta-meta">'+
          '<span class="save-cta-price">'+money(lastResult.price)+'</span>'+
          '<span class="res-stock"><span class="dot"></span>'+lastResult.stock.toLocaleString("en-US")+' in stock</span>'+
        '</div>'+
      '</div>'+
      '<div class="save-cta-acts">'+
        '<a class="btn btn-amber" href="'+esc(cartLink([[lastResult.variantId,1]], lastResult.url))+'">Add to cart</a>'+
        '<a class="btn" href="' + L_SUPPORT + '">Talk to an engineer first</a>'+
      '</div>'+
    '</div>'+
      '</div>';

    var amtEl = document.getElementById("cueAmt");
    if(amtEl) amtEl.textContent = API.usd(res.annual);
    var stEl = document.getElementById("cueState");
    if(stEl) stEl.textContent = st[1];
    var payEl = document.getElementById("cuePay");
    if(payEl) payEl.textContent = res.payback > 0 ? ("Pays for itself in " + res.payback.toFixed(1) + " years") : "";

    var selEl = document.getElementById("resState");
    if(selEl){ selEl.addEventListener("change", function(){ savedState = this.value; renderSavings(); }); }
  }

  document.addEventListener("input", function(e){
    if(!e.target || e.target.id !== "numIn") return;
    var s = activeSteps()[state.step];
    if(!s || s.type !== "num") return;
    var v = parseInt(e.target.value,10);
    var out = document.getElementById("numOut"), hint = document.getElementById("numHint");
    if(out)  out.textContent  = v.toLocaleString("en-US");
    if(hint) hint.textContent = s.hint(v, state.answers);
  });

  document.addEventListener("click", function(e){
    /* ---- Racking picker. Only ever re-renders its own block, so the
            recommendation above does not jump under the reader. ---- */
    var a = e.target.closest ? e.target.closest("[data-arack],[data-aclear]") : null;
    if(a){
      e.preventDefault();
      if(a.hasAttribute("data-arack")){
        var nr = parseInt(a.getAttribute("data-arack"),10);
        addon.rackId = (addon.rackId === nr) ? null : nr;   /* clicking it again clears it */
      } else {
        resetAddons();
      }
      renderAddons();
      return;
    }

    var t = e.target.closest ? e.target.closest("[data-start],[data-answer],[data-back],[data-restart],[data-track],[data-scroll-save],[data-num],[data-pbuy]") : null;
    if(!t) return;

    if(t.hasAttribute("data-scroll-save")){
      var tgt = document.getElementById("resSave");
      if(tgt) tgt.scrollIntoView({behavior:"smooth", block:"start"});
      return;
    }

    if(t.hasAttribute("data-track")){
      e.preventDefault();
      state = {track:t.getAttribute("data-track"), step:0, answers:{}, pumpBuy:null};
      resetAddons();
      renderStep();
      document.getElementById("selector").scrollIntoView({behavior:"smooth", block:"start"});
      return;
    }
    if(t.hasAttribute("data-start")){
      state = {track:t.getAttribute("data-start"), step:0, answers:{}, pumpBuy:null};
      resetAddons();
      renderStep(); return;
    }
    if(t.hasAttribute("data-answer")){
      var s = activeSteps()[state.step];
      var raw = t.getAttribute("data-answer");
      var val = /^\d+$/.test(raw) ? parseInt(raw,10) : raw;
      state.answers[s.id] = val;
      state.pumpBuy = null;
      resetAddons();
      state.step++;
      renderStep(); return;
    }
    if(t.hasAttribute("data-pbuy")){
      state.pumpBuy = t.getAttribute("data-pbuy");
      renderResult(); return;
    }
    if(t.hasAttribute("data-num")){
      var ns = activeSteps()[state.step];
      var el = document.getElementById("numIn");
      state.answers[ns.id] = el ? parseInt(el.value,10) : ns.val;
      state.pumpBuy = null;
      resetAddons();
      state.step++;
      renderStep(); return;
    }
    if(t.hasAttribute("data-back")){
      state.step--;
      if(state.step < 0){ state.track = null; renderStart(); } else { renderStep(); }
      return;
    }
    if(t.hasAttribute("data-restart")){
      state = {track:null, step:0, answers:{}, pumpBuy:null};
      resetAddons();
      renderStart(); return;
    }
  });

  /* Shopify handles the commercial enquiry itself and sends the customer back
     here with contact_posted=true on the URL. Without this they would land on a
     blank finder and have no idea whether it went. */
  function postedConfirmation(){
    if(document.location.search.indexOf("contact_posted=true") === -1) return false;
    stage.textContent = "Commercial project — sent";
    crumb.innerHTML = "";
    body.innerHTML =
      '<div class="res-because"><b>That is with the engineering desk</b>' +
        'Your answers are in. A specialist reads them and replies within <b>two business days</b> with a ' +
        'single-line diagram, an equipment list and a production estimate run against your own tariff. ' +
        'If the site needs a licensed designer rather than us, they will say so rather than quoting you anyway.</div>' +
      '<div class="res-alt" style="margin-top:24px;max-width:46rem">' +
        '<h4>One thing will move this faster than anything else</h4>' +
        '<p>Reply to the confirmation email with <b>twelve months of utility bills</b> attached. ' +
        'Commercial tariffs bill demand separately from energy, and on many sites the demand charge is the ' +
        'larger number — a system sized without seeing it can miss most of the saving.</p></div>' +
      '<div class="res-acts" style="margin-top:24px">' +
        '<a class="btn btn-amber" href="' + L_CATALOG + '">Browse the range meanwhile</a>' +
        '<button class="sel-back" type="button" data-restart="1">← Size something else</button>' +
      '</div>';
    return true;
  }

  /* A link can open the finder on a track — /pages/finder?start=pool — so the
     homepage's three doors land on the first real question instead of asking
     it again. Anything but the three known tracks falls back to the start. */
  function startFromUrl(){
    var m = /[?&]start=(system|well|pool)\b/.exec(String(location.search || ""));
    if(!m) return false;
    state = {track:m[1], step:0, answers:{}, pumpBuy:null};
    resetAddons();
    renderStep();
    return true;
  }

  if(!postedConfirmation()){ if(!startFromUrl()) renderStart(); }
};

/* Both halves are defined; hand the tables to the engine. If the adapter bailed
   out — no data element, or JSON that would not parse — RS_TABLES is undefined
   and the finder stays as the server rendered it, with the fallback message the
   adapter wrote into #selBody. */
if (window.RS_TABLES) { window.RS_ENGINE(window.RS_TABLES); }
